/**
 * Pure helpers for the `/ship` station (P5 S3) — the queue derivation, the buy-gate mirror, the
 * parcel estimate and the scan-result copy. IO-free so every rule is unit-testable, in the same
 * split as `scan-lib` / `press-station-lib`: this file decides, the page renders.
 *
 * ── The one thing to know before reading ──
 * The station's queue is NOT the board's `readyToShip` lane. That lane holds caps (`PRODUCED`) and
 * Ship-only goods (`PACKED`) only; a printed garment order — the shop's core volume — rolls up
 * `PRINTED` and lands in the lane the board calls "Printed · to ship". Both are ship-ready, so the
 * station reads BOTH. (Verified against `production-board.ts`'s lane switch, 2026-08-26.)
 *
 * Held orders stay VISIBLE (the mockup's design-of-record): an order with an outstanding reprint is
 * demoted by the board out of the ship lanes into `inProduction`, but it keeps its real
 * `production.status` — so a demoted PRINTED/PRODUCED/PACKED order is exactly "would ship but for
 * the reprint" and is listed here, blocked, rather than vanishing from the operator's world.
 */
import type { components } from '@/lib/api/types.gen';

export type ProductionBoard = components['schemas']['ProductionBoardResponse'];
export type ProductionBoardOrder = components['schemas']['ProductionBoardOrder'];
export type ShipManifest = components['schemas']['ShipmentManifestResponse'];
export type ShipManifestLine = ShipManifest['lines'][number];
export type Shipment = ShipManifest['shipments'][number];
export type ShipmentRate = Shipment['rates'][number];
export type ShipParcel = Shipment['parcel'];
export type VerifyStatus = components['schemas']['ShipVerifyResponse']['status'];
/** A verify request: EITHER a scanned code OR an absolute eye-check count — a real union, so the
 *  page can't send half of each. */
export type ShipVerifyRequest = components['schemas']['ShipVerifyRequest'];
/** The per-line tally `POST …/verify` echoes back — the manifest line MINUS its display fields. */
export type ShipVerifyProgress = components['schemas']['ShipVerifyResponse']['lines'][number];

/** The rollup statuses that mean "production is done — this order can ship". */
const SHIP_READY_STATUSES = new Set(['PRINTED', 'PRODUCED', 'PACKED']);

export type ShipQueueState = 'ready' | 'held';

export type ShipQueueRow = {
  order: ProductionBoardOrder;
  state: ShipQueueState;
  /** Why it can't ship, when held. Null when ready. */
  heldReason: string | null;
};

/**
 * Every order the station may work, ready and held together, in the board's own priority order.
 *
 * Ordering deliberately MIRRORS `compareBoardOrders` (rush first, then earliest due date, then
 * FIFO) rather than the plan's shorthand "oldest first": the station and the board must agree on
 * which order is next, or the two surfaces argue about priority in front of the operator. FIFO is
 * what that ordering degrades to when nothing is rushed or dated, which is the common case.
 */
export function shipQueue(board: ProductionBoard): ShipQueueRow[] {
  const rows: ShipQueueRow[] = [];
  const ready = [...board.printed, ...board.readyToShip];
  for (const order of ready) {
    rows.push({ order, state: 'ready', heldReason: null });
  }
  // Demoted-but-ship-ready orders: the board moved them to `inProduction` because of the reprint
  // gate, but their rollup status still says production is finished.
  for (const order of board.inProduction) {
    if (!SHIP_READY_STATUSES.has(order.production.status)) continue;
    rows.push({
      order,
      state: 'held',
      heldReason: board.reprintCheckFailed
        ? 'Reprint check unavailable — held until it recovers'
        : reprintHeldReason(order.reprintOutstanding),
    });
  }
  return rows.sort(compareQueueRows);
}

function reprintHeldReason(outstanding: number): string {
  if (outstanding <= 0) return 'Held — production reports it is not clear to ship';
  return `${outstanding} reprint${outstanding === 1 ? '' : 's'} outstanding`;
}

/** Board ordering: rush ↓, due date ↑ (undated last), FIFO, then a stable id tiebreak. */
export function compareQueueRows(a: ShipQueueRow, b: ShipQueueRow): number {
  const x = a.order;
  const y = b.order;
  if ((x.rush ?? false) !== (y.rush ?? false)) return x.rush ? -1 : 1;
  const dx = x.dueDate ?? null;
  const dy = y.dueDate ?? null;
  if (dx !== dy) {
    if (dx === null) return 1;
    if (dy === null) return -1;
    return dx.localeCompare(dy);
  }
  const created = x.shopifyCreatedAt.localeCompare(y.shopifyCreatedAt);
  if (created !== 0) return created;
  return `${x.storeId}#${x.orderId}`.localeCompare(`${y.storeId}#${y.orderId}`);
}

/**
 * The queue row's one-line description. Counts LINES, plus printed garments when there are any.
 *
 * ⚠️ Deliberately not "N garments": the board's `pieces` counts DTF garments only — it excludes
 * cancelled and non-DTF lines (decals, stickers) — while the pack screen counts every owed unit it
 * will make you verify. An order of 2 shirts + 3 decals would read "2 garments" here and "5" there.
 * Rather than print two different numbers under the same word, the queue names what it is counting.
 */
export function queueRowSummary(order: ProductionBoardOrder): string {
  const p = order.production;
  const parts = [`${p.totalLines} line${p.totalLines === 1 ? '' : 's'}`];
  if (p.pieces > 0) parts.push(`${p.pieces} printed garment${p.pieces === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/** The chip that says what KIND of ship-ready this is (they pack differently). */
export function queueRowChip(order: ProductionBoardOrder): string {
  switch (order.production.status) {
    case 'PRINTED':
      return 'Printed';
    case 'PRODUCED':
      return 'Produced';
    case 'PACKED':
      return 'Ship-only';
    default:
      return order.production.status;
  }
}

/** Whole-day age, for the queue's "2d" column. Same shape the board's age column uses. */
export function ageLabel(iso: string, now: number): string {
  const ms = now - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return 'new';
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// ─────────────────────────────────────────────────────────────────────────────
// The buy gate — a MIRROR of the server's refusal, never the authority
// ─────────────────────────────────────────────────────────────────────────────

export type BuyGate = {
  /** Every server-side condition is satisfied as far as this client can see. */
  armed: boolean;
  /** Operator-facing reasons the buy is refused, in the order they should be fixed. */
  blockers: string[];
  /** What the buy button says right now. */
  label: string;
};

/**
 * Mirror the server's buy gate so the button can explain itself BEFORE the operator taps.
 *
 * ⚠️ This is a mirror, not the gate. `POST …/buy` re-checks every condition with a strongly
 * consistent read and refuses on its own authority; if the two ever disagree, the server wins and
 * the page shows its refusal. Never relax this into "the button was enabled, so the buy is fine".
 */
export type BuyGateContext = {
  /** The parcel on screen differs from the one the quoted rate was priced for. */
  parcelChanged?: boolean;
  /** A re-rate is in flight — the rates on screen are about to be replaced. */
  quoting?: boolean;
};

export function buyGate(
  manifest: ShipManifest,
  chosenRate: ShipmentRate | null,
  ctx: BuyGateContext = {},
): BuyGate {
  const blockers: string[] = [];
  const missing = remainingToVerify(manifest.lines);
  if (missing > 0) blockers.push(`${missing} garment${missing === 1 ? '' : 's'} still to verify`);
  if (manifest.reprintOutstanding > 0) {
    blockers.push(
      `${manifest.reprintOutstanding} reprint${manifest.reprintOutstanding === 1 ? '' : 's'} outstanding`,
    );
  }
  if (manifest.orderFulfillmentState !== 'UNFULFILLED') {
    blockers.push(
      manifest.orderFulfillmentState === 'FULFILLED'
        ? 'Shopify already shows this order fulfilled'
        : 'Order is cancelled',
    );
  }
  if (!manifest.shipFromConfigured)
    blockers.push('Shop address is not set (Settings › Purchasing › Ship-to address)');
  if (totalOwed(manifest.lines) === 0) blockers.push('Nothing on this order is shippable');

  const armed = blockers.length === 0 && chosenRate !== null;
  if (blockers.length > 0) {
    // The verification count is the one the operator can act on right now, so it leads.
    return { armed: false, blockers, label: missing > 0 ? verifyMoreLabel(missing) : 'Locked' };
  }
  if (!chosenRate) return { armed: false, blockers, label: 'Rate the parcel first' };
  // A rate prices ONE parcel. Editing weight or box after rating leaves the old price armed, and
  // buying it ships an 8 oz label on a 40 oz box — the carrier re-weighs and bills the difference.
  if (ctx.parcelChanged) {
    return {
      armed: false,
      blockers: ['The parcel changed since it was rated'],
      label: 'Re-rate the parcel',
    };
  }
  if (ctx.quoting) return { armed: false, blockers, label: 'Rating…' };
  return { armed, blockers, label: `Buy label · ${formatUsd(chosenRate.rate)}` };
}

function verifyMoreLabel(missing: number): string {
  return `Verify ${missing} more garment${missing === 1 ? '' : 's'}`;
}

export function remainingToVerify(lines: readonly ShipManifestLine[]): number {
  return lines.reduce((sum, l) => sum + Math.max(0, l.owed - l.verified), 0);
}

export function totalOwed(lines: readonly ShipManifestLine[]): number {
  return lines.reduce((sum, l) => sum + l.owed, 0);
}

export function totalVerified(lines: readonly ShipManifestLine[]): number {
  return lines.reduce((sum, l) => sum + Math.min(l.owed, l.verified), 0);
}

/** How one garment of a line got verified. */
export type VerifyPipState = 'scanned' | 'eye' | 'open';

/** One pip per owed garment. `rank` is the garment's 1-based owed-rank — the same ordinal the QR
 *  encodes — so the pip has a real identity to be keyed and labelled by, not just a position. */
export type VerifyPip = { rank: number; state: VerifyPipState };

export function linePips(line: ShipManifestLine): VerifyPip[] {
  const pips: VerifyPip[] = [];
  const scanned = Math.min(line.owed, line.scanned);
  const eye = Math.min(line.owed - scanned, line.eyeChecked);
  for (let i = 0; i < line.owed; i++) {
    pips.push({
      rank: i + 1,
      state: i < scanned ? 'scanned' : i < scanned + eye ? 'eye' : 'open',
    });
  }
  return pips;
}

// ─────────────────────────────────────────────────────────────────────────────
// Parcel — the estimate the operator corrects, never a silent guess
// ─────────────────────────────────────────────────────────────────────────────

/** Measured: 88.3% of a year's labels were ONE garment in a poly mailer at a flat 8 oz. */
export const MAILER_TARE_OZ = 3;
export const GARMENT_OZ = 5;

export type BoxPreset = {
  id: string;
  label: string;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
};

/** Dims mirror the real parcel profile in the 12-month export (99.4% carried dims). */
export const BOX_PRESETS: BoxPreset[] = [
  { id: 'mailer-sm', label: 'Poly mailer 12×15.5', lengthIn: 12, widthIn: 15.5, heightIn: 1 },
  { id: 'mailer-lg', label: 'Poly mailer 14.5×19', lengthIn: 14.5, widthIn: 19, heightIn: 2 },
  { id: 'box-sm', label: 'Box 12×9×4', lengthIn: 12, widthIn: 9, heightIn: 4 },
  { id: 'box-team', label: 'Team box 18×14×12', lengthIn: 18, widthIn: 14, heightIn: 12 },
];

/**
 * A starting parcel for the order: the mailer, plus 5 oz a garment over a 3 oz tare — which lands a
 * single-garment order on exactly the 8 oz the measured cohort actually shipped. It is a PREFILL:
 * the operator edits it against the bench scale, and the carrier bills on its own re-weigh anyway.
 */
export function estimateParcel(manifest: ShipManifest): ShipParcel {
  const units = totalOwed(manifest.lines);
  const preset = BOX_PRESETS[0] as BoxPreset;
  return {
    weightOz: Math.max(1, MAILER_TARE_OZ + GARMENT_OZ * units),
    lengthIn: preset.lengthIn,
    widthIn: preset.widthIn,
    heightIn: preset.heightIn,
  };
}

/** Whether two parcels are the same box+weight — i.e. whether a quoted rate still prices THIS parcel. */
export function sameParcel(a: ShipParcel, b: ShipParcel): boolean {
  return (
    a.weightOz === b.weightOz &&
    a.lengthIn === b.lengthIn &&
    a.widthIn === b.widthIn &&
    a.heightIn === b.heightIn
  );
}

/** A weight the provider will accept — `ShipParcelSchema` requires positive, so 0/blank is refused. */
export function isRatableParcel(p: ShipParcel): boolean {
  return p.weightOz >= 1 && p.lengthIn > 0 && p.widthIn > 0 && p.heightIn > 0;
}

/** "1 lb 10 oz" / "8 oz" — the way a shop floor reads a scale, not decimal ounces. */
export function formatWeight(oz: number): string {
  if (!Number.isFinite(oz) || oz <= 0) return '—';
  const whole = Math.round(oz * 10) / 10;
  if (whole < 16) return `${trimZero(whole)} oz`;
  const lb = Math.floor(whole / 16);
  const rest = Math.round((whole - lb * 16) * 10) / 10;
  return rest === 0 ? `${lb} lb` : `${lb} lb ${trimZero(rest)} oz`;
}

function trimZero(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/** Cheapest first — the station's default choice, and the flag that marks it in the list. */
export function ratesCheapestFirst(rates: readonly ShipmentRate[]): ShipmentRate[] {
  return [...rates].sort((a, b) => a.rate - b.rate || a.service.localeCompare(b.service));
}

export function rateEta(rate: ShipmentRate): string | null {
  const days = rate.deliveryDays;
  if (days == null || days <= 0) return null;
  return `${days}d`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage + shipment selection
// ─────────────────────────────────────────────────────────────────────────────

/** The shipment whose label is live on this order (what the "after the buy" screen acts on). */
export function boughtShipment(manifest: ShipManifest): Shipment | null {
  const bought = manifest.shipments.filter((s) => s.status === 'BOUGHT');
  return bought.length === 0 ? null : (bought[bought.length - 1] as Shipment);
}

/** A shipment whose purchase is still unresolved — the station must not start a new one beside it. */
export function unresolvedShipment(manifest: ShipManifest): Shipment | null {
  return (
    manifest.shipments.find(
      (s) => s.buyAttemptStatus === 'in_flight' || s.buyAttemptStatus === 'unknown',
    ) ?? null
  );
}

/** The freshest QUOTED shipment — the one a buy would spend against. */
export function quotedShipment(manifest: ShipManifest): Shipment | null {
  const quoted = manifest.shipments.filter((s) => s.status === 'QUOTED');
  return quoted.length === 0 ? null : (quoted[quoted.length - 1] as Shipment);
}

/**
 * Whether Shopify actually holds the fulfillment. ⚠️ The marker's PRESENCE is not the answer: the
 * server claims it with `fulfillmentIds: []` BEFORE the mutation and deliberately KEEPS it when the
 * outcome is ambiguous, so a present-but-empty marker means "we tried, Shopify may not have it" —
 * exactly the state that must still offer a retry rather than tell the operator the customer was
 * emailed. The server's own discriminator is the id list, so this mirrors it.
 */
export function fulfillmentState(manifest: ShipManifest): 'none' | 'pending' | 'created' {
  const marker = manifest.fulfillment;
  if (!marker) return 'none';
  return marker.fulfillmentIds.length > 0 ? 'created' : 'pending';
}

export type ShipStage = 'pack' | 'done';

export function shipStage(manifest: ShipManifest): ShipStage {
  return boughtShipment(manifest) ? 'done' : 'pack';
}

/**
 * A TEST-mode label can never be pushed to Shopify (the server refuses it — a fake tracking number
 * must not reach a customer). The station says so up front instead of letting Fulfill 422.
 */
export function isTestLabel(shipment: Shipment): boolean {
  return shipment.providerMode !== 'production';
}

// ─────────────────────────────────────────────────────────────────────────────
// Scan feedback
// ─────────────────────────────────────────────────────────────────────────────

export type ShipScanTone = 'success' | 'warn' | 'error' | 'info';

export type ShipScanView = {
  tone: ShipScanTone;
  title: string;
  detail: string;
  /** A wrong-order scan is the one an operator must not be able to miss. */
  loud: boolean;
};

/**
 * Operator-facing copy for every scan outcome. Mirrors `/scan`'s posture: a non-counting scan is a
 * STATE with an instruction, never an error dialog — the scanner is a firehose of foreign barcodes.
 */
export function shipScanView(
  status: VerifyStatus,
  ctx: { scannedOrderName?: string | undefined } = {},
): ShipScanView {
  switch (status) {
    case 'COUNTED':
      return { tone: 'success', title: 'Counted', detail: 'Garment is in the box.', loud: false };
    case 'EYE_COUNTED':
      return {
        tone: 'success',
        title: 'Eye-check recorded',
        detail: 'Logged against the line — who and when.',
        loud: false,
      };
    case 'DUPLICATE':
      return {
        tone: 'warn',
        title: 'Already counted',
        detail: 'This exact garment is already in the box — nothing was added.',
        loud: false,
      };
    case 'NOT_OWED':
      return {
        tone: 'warn',
        title: 'Refunded garment',
        detail: 'This copy is no longer owed on the order — do not ship it.',
        loud: false,
      };
    case 'WRONG_ORDER':
      return {
        tone: 'error',
        title: 'Wrong order',
        detail: ctx.scannedOrderName
          ? `That garment belongs to ${ctx.scannedOrderName}. Put it back on its pile — nothing was counted.`
          : 'That garment belongs to a different order. Put it back on its pile — nothing was counted.',
        loud: true,
      };
    case 'LINE_NOT_FOUND':
      return {
        tone: 'warn',
        title: 'Not on this order',
        detail: 'The tag decodes, but its line is not on this order any more.',
        loud: false,
      };
    default:
      return {
        tone: 'info',
        title: 'Unrecognized code',
        detail: 'Not an FSP garment tag — scan the tag printed with the sheet.',
        loud: false,
      };
  }
}

/** Tagless units (a reissue prints no tag) are verified by a logged human count instead of a scan. */
export function needsEyeCheck(line: ShipManifestLine): boolean {
  return line.owed > line.scanned + line.eyeChecked;
}
